import * as React from 'react';
import classnames from 'classnames';
import { useEventListener } from '../hooks';
import Flex, { Props as FlexProps } from '../Flex/Flex';
import { ModalContext } from './ModalContext';
import Portal from '../Portal/Portal';
import { TypeColors } from '../Base/Base';
import './Modal.css';

const { useEffect, useState } = React;

const enableScroll = () => document.body.classList.remove('Body--modal-open');
const disableScroll = () => document.body.classList.add('Body--modal-open');

export interface Props extends FlexProps {
  /**
   * Background color of the dialog box
   */
  backgroundColor?: TypeColors;
  /**
   * Flag to set the initial height of the dialog box to
   * the full height available.
   */
  fullHeight?: boolean;
  /**
   * Callback that is called when clicking on the overlay
   * area, useful for dismissing the modal.
   */
  onClose?: () => void;
  /**
   * Flag that controls the rendering and visibility of the
   * modal to the user.
   */
  visible: boolean;
  /**
   * Width of the dialog box.
   */
  width: string;
}

/**
 * Modal component with overlay for interupting a user with
 * an important message or question.
 */
const Modal: React.FunctionComponent<Props> = (props: Props) => {
  const {
    backgroundColor,
    children,
    fullHeight,
    onClose,
    visible,
    width,
    ...rest
  } = props;

  const [modalElement, setModalElement] = useState<HTMLElement | null>(null);
  const [render, setRender] = useState(props.visible);

  const classes = classnames('Modal', {
    'Modal--visible': visible,
  });

  const handleClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
  };

  useEffect(() => {
    if (visible) {
      setRender(true);
      disableScroll();
    } else {
      enableScroll();
    }
  }, [visible]);

  useEventListener(modalElement, 'animationend', () => {
    if (!visible) {
      setRender(false);
      setModalElement(null);
    }
  }, [visible]);

  if (!render) {
    return null;
  }

  return (
    <Portal element={ document.body }>
      <Flex
          alignChildrenHorizontal="middle"
          className={ classes }
          direction="vertical"
          innerRef={ setModalElement }
          onClick={ onClose }
          padding="x6">
        <Flex
            backgroundColor={ backgroundColor }
            borderRadius="x2"
            className="Modal__content"
            direction="vertical"
            grow={ fullHeight }
            onClick={ handleClick }
            width={ width }
            { ...rest }>
          <ModalContext.Provider value={ { modalElement, onClose } }>
            { children }
          </ModalContext.Provider>
        </Flex>
      </Flex>
    </Portal>
  );
};

Modal.defaultProps = {
  backgroundColor: 'gray-1',
};

export default Modal;
